import { WalletPassData } from "sharedTypes"
import {
  defaultWalletPassBuilderValues,
  normalizeBackSectionValue,
  DEFAULT_SECONDARY_LABEL,
  DEFAULT_GUEST_NAME_LABEL,
  GOOGLE_DEFAULT_ISSUER_NAME,
  PASS_BACK_SECTION_TYPES,
  PassBackSection,
  PassFieldValueSource,
  PassFieldMode,
  PassBackSectionType,
  WalletPassBuilderFormValues,
  resolveFieldPreviewValue,
  resolveAuxiliaryPreviewFields,
  DEFAULT_HORIZONTAL_LOGO_URL,
  DEFAULT_SQUARE_LOGO_URL,
  PLACEHOLDER_GUEST_NAME,
} from "./walletPassBuilderTypes"

const VALUE_SOURCES: PassFieldValueSource[] = [
  "static",
  "event_name",
  "guest_name",
  "guest_email",
  "ticket_id",
  "form_name",
  "event_venue",
  "hide",
]

// A hidden field carries no source of its own — the toggle owns hiding now — so
// it opens on Static Text and its text box is there the moment it is switched on.
const asShownValueSource = (
  source: string | undefined,
  fallback: PassFieldValueSource,
): PassFieldValueSource => (
  source === "hide" ? "static" : asValueSource(source, fallback)
)

const asValueSource = (
  value: string | undefined,
  fallback: PassFieldValueSource,
): PassFieldValueSource => (
  VALUE_SOURCES.includes(value as PassFieldValueSource)
    ? value as PassFieldValueSource
    : fallback
)

const asBackSectionType = (value: string | undefined): PassBackSectionType | "" => (
  PASS_BACK_SECTION_TYPES.includes(value as PassBackSectionType)
    ? value as PassBackSectionType
    : ""
)

const mapBackSections = (sections: WalletPassData["backSections"]): PassBackSection[] => (
  (sections || []).map((section, index) => ({
    id: section.id || `back-${index + 1}`,
    type: asBackSectionType(section.type),
    label: section.label || "",
    value: section.value || "",
  }))
)

// The Diobox logos WalletPass::Customization#apply_default_branding puts on an
// otherwise unbranded pass. Seeding them into the form keeps the upload slots,
// the previews, and the saved data showing the same thing, and saving writes
// them out — which brands the pass, so the API stops re-injecting and a removed
// horizontal logo stays removed.
const withDefaultBranding = (
  values: WalletPassBuilderFormValues,
): WalletPassBuilderFormValues => {
  const branded = values.squareLogoUrl || values.horizontalLogoUrl || values.logoText
    || values.appleBannerUrl || values.googleHeroUrl

  return branded ? values : {
    ...values,
    squareLogoUrl: DEFAULT_SQUARE_LOGO_URL,
    horizontalLogoUrl: DEFAULT_HORIZONTAL_LOGO_URL,
  }
}

/** A field with nothing of its own — no static text, no source but its default
 *  — is what makes the pass follow the event, so that is the "default" mode
 *  rather than a stored flag. `label` is only passed for the Header Field,
 *  whose label is the event's start time rather than decoration of the
 *  organizer's own, and so counts as an override too. */
const fieldModeFor = (
  { source, staticValue, label }: {
    source?: string
    staticValue?: string
    label?: string
  },
  defaultSource: string,
): PassFieldMode => (
  (source && source !== defaultSource) || staticValue || label ? "override" : "default"
)

/** The single rule for what a field resolves to, used for both the preview and
 *  the save so the two cannot disagree — which is the bug DIO-992 was. */
const resolveFieldMode = (
  mode: PassFieldMode,
  staticValue: string | undefined,
  defaultSource: PassFieldValueSource,
): { valueSource: PassFieldValueSource; staticValue: string | undefined } => (
  mode === "override"
    ? { valueSource: "static", staticValue: staticValue || undefined }
    : { valueSource: defaultSource, staticValue: undefined }
)

/** Event Title and Event Location differ only by which stored keys they read
 *  and which default they fall back to. */
const SYMMETRIC_FIELDS = [
  {
    mode: "eventTitleMode",
    source: "primaryValueSource",
    staticValue: "primaryStaticValue",
    defaultSource: "event_name",
  },
  {
    mode: "eventLocationMode",
    source: "secondaryValueSource",
    staticValue: "secondaryStaticValue",
    defaultSource: "event_venue",
  },
] as const

export const walletPassDataToFormValues = (
  data?: WalletPassData | null,
): WalletPassBuilderFormValues => {
  const defaults = defaultWalletPassBuilderValues()
  if (!data || Object.keys(data).length === 0) {
    return withDefaultBranding(defaults)
  }

  const stored = data

  return withDefaultBranding({
    ...defaults,
    squareLogoUrl: stored.squareLogoUrl ?? defaults.squareLogoUrl,
    horizontalLogoUrl: data.horizontalLogoUrl ?? defaults.horizontalLogoUrl,
    appleBannerUrl: data.appleBannerUrl ?? defaults.appleBannerUrl,
    googleHeroUrl: data.googleHeroUrl ?? defaults.googleHeroUrl,
    backgroundColor: data.backgroundColor ?? defaults.backgroundColor,
    textColor: data.textColor ?? defaults.textColor,
    labelColor: data.labelColor ?? defaults.labelColor,
    brandNameMode: data.logoText ? "override" : "default",
    logoText: data.logoText ?? defaults.logoText,
    headerMode: fieldModeFor({
      source: stored.headerValueSource,
      staticValue: stored.headerStaticValue,
      label: stored.headerLabel,
    }, "static"),
    headerLabel: stored.headerLabel ?? defaults.headerLabel,
    headerValueSource: asValueSource(stored.headerValueSource, defaults.headerValueSource),
    headerStaticValue: stored.headerStaticValue ?? defaults.headerStaticValue,
    eventTitleMode: fieldModeFor(
      { source: stored.primaryValueSource, staticValue: stored.primaryStaticValue },
      "event_name",
    ),
    primaryLabel: stored.primaryLabel ?? defaults.primaryLabel,
    primaryValueSource: asValueSource(stored.primaryValueSource, defaults.primaryValueSource),
    primaryStaticValue: stored.primaryStaticValue ?? defaults.primaryStaticValue,
    primaryMovedBelowBanner: stored.primaryMovedBelowBanner ?? defaults.primaryMovedBelowBanner,
    eventLocationMode: fieldModeFor(
      { source: stored.secondaryValueSource, staticValue: stored.secondaryStaticValue },
      "event_venue",
    ),
    secondaryLabel: stored.secondaryLabel ?? defaults.secondaryLabel,
    secondaryValueSource: asValueSource(stored.secondaryValueSource, defaults.secondaryValueSource),
    secondaryStaticValue: stored.secondaryStaticValue ?? defaults.secondaryStaticValue,
    auxiliary1Enabled: (stored.auxiliary1ValueSource ?? defaults.auxiliary1ValueSource) !== "hide",
    auxiliary1Label: stored.auxiliary1Label ?? defaults.auxiliary1Label,
    auxiliary1ValueSource: asShownValueSource(
      stored.auxiliary1ValueSource,
      defaults.auxiliary1ValueSource,
    ),
    auxiliary1StaticValue: stored.auxiliary1StaticValue ?? defaults.auxiliary1StaticValue,
    auxiliary2Enabled: (stored.auxiliary2ValueSource ?? defaults.auxiliary2ValueSource) !== "hide",
    auxiliary2Label: stored.auxiliary2Label ?? defaults.auxiliary2Label,
    auxiliary2ValueSource: asShownValueSource(
      stored.auxiliary2ValueSource,
      defaults.auxiliary2ValueSource,
    ),
    auxiliary2StaticValue: stored.auxiliary2StaticValue ?? defaults.auxiliary2StaticValue,
    guestNameEnabled: stored.guestNameEnabled ?? defaults.guestNameEnabled,
    guestNameLabel: stored.guestNameLabel ?? defaults.guestNameLabel,
    qrCodeEnabled: stored.qrCodeEnabled ?? defaults.qrCodeEnabled,
    backSections: stored.backSections?.length
      ? mapBackSections(stored.backSections)
      : defaults.backSections,
  })
}

// The preview shows the real event start in the event's timezone. Google
// renders the event ticket's date and time as two separate fields.
const formatEventPart = (
  startDate: string | undefined,
  timezone: string | undefined,
  options: Intl.DateTimeFormatOptions,
) => {
  if (!startDate) return ""
  try {
    return new Intl.DateTimeFormat("en-US", {
      ...options,
      timeZone: timezone || undefined,
    }).format(new Date(startDate))
  } catch {
    return ""
  }
}

// No year: matches the event ticket's DATE_ONLY card rendering (e.g. "Jun 14").
export const formatEventDate = (startDate?: string, timezone?: string) => (
  formatEventPart(startDate, timezone, { month: "short", day: "numeric" })
)

export const formatEventTime = (startDate?: string, timezone?: string) => (
  formatEventPart(startDate, timezone, { hour: "numeric", minute: "2-digit" })
)

// Mirrors Event#full_address, minus the leading space Ruby leaves on a lone zipcode.
export const formatEventAddress = (event: {
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  zipcode?: string
}): string => {
  const fields = [event.addressLine1, event.addressLine2, event.city, event.state]
    .filter(Boolean)
    .join(", ")

  return (event.zipcode ? `${fields} ${event.zipcode}` : fields).trim()
}

export type PassPreviewEventInfo = {
  name?: string
  accountName?: string
  dateText?: string
  timeText?: string
  venue?: string
  address?: string
}

// Maps builder form values (plus event-level info) into the props for every
// wallet pass preview. Shared by the builder and the settings page so the two
// always render identically.
export const resolvePassPreviewProps = (
  values: WalletPassBuilderFormValues,
  event: PassPreviewEventInfo = {},
) => {
  // Blank rows are dropped on save, so previewing them would show a card the
  // pass never carries — including while a freshly added row is still empty.
  const backSections = (values.backSections || [])
    .filter((section) => section.label.trim() || section.value.trim())
    .map((section) => ({
      label: section.label,
      value: section.value,
      type: section.type,
    }))

  // Default branding is resolved once, when the form values are built, so what
  // arrives here is already what the pass will carry.
  const { squareLogoUrl, horizontalLogoUrl } = values

  const appleLogoUrl = horizontalLogoUrl || squareLogoUrl || undefined
  const googleLogoUrl = squareLogoUrl || undefined
  // Google has no horizontal logo slot, so it keeps its logo text.
  // Under "Use Account Name" nothing is stored, so Apple prints no brand text
  // and Google falls back to the account, exactly as the two services do.
  const brandName = values.brandNameMode === "override" ? values.logoText : ""
  const appleBrandName = horizontalLogoUrl ? "" : brandName
  // Google requires an issuer name, so a blank Brand Name falls back to the
  // event's own title, as GooglePass#brand_name does.
  const googleBrandName = brandName || event.accountName || GOOGLE_DEFAULT_ISSUER_NAME
  const fieldEvent = { name: event.name, venue: event.venue, address: event.address }
  const auxiliaryFields = resolveAuxiliaryPreviewFields(values, fieldEvent)
  // Google prints the ticket holder itself, so Apple's guest row is what keeps
  // the name off the Google pass twice. Hiding it takes the name off both.
  const showGuestName = values.guestNameEnabled
  const guestNameField = showGuestName
    ? [{ label: values.guestNameLabel || DEFAULT_GUEST_NAME_LABEL, value: PLACEHOLDER_GUEST_NAME }]
    : []
  const appleAuxiliaryFields = [...guestNameField, ...auxiliaryFields]
  // Apple only: with the flag set, the banner sits where Primary would, so drop
  // Primary from the Apple front — its content is mirrored into Aux 1. Google
  // never renders Primary at all.
  const primaryHiddenForBanner = values.primaryMovedBelowBanner && Boolean(values.appleBannerUrl)

  // An unset header falls back to the event's start time and date, as
  // ApplePass#header_fields does. "Event date and time" is that same empty
  // header, so anything typed under Override stays out of the preview until the
  // organizer switches back to it.
  const customHeader = values.headerMode === "override"
  // Resolved through the same rule the save uses, so the preview shows exactly
  // what gets stored.
  const [eventTitleText, eventLocationText] = SYMMETRIC_FIELDS.map((field) => {
    const resolved = resolveFieldMode(
      values[field.mode], values[field.staticValue], field.defaultSource,
    )

    return resolveFieldPreviewValue(resolved.valueSource, resolved.staticValue || "", fieldEvent)
  })
  const applePreviewProps = {
    brandName: appleBrandName,
    logoIsHorizontal: Boolean(horizontalLogoUrl),
    timeText: customHeader ? values.headerLabel : event.timeText || "",
    dateText: customHeader
      ? resolveFieldPreviewValue(values.headerValueSource, values.headerStaticValue, fieldEvent)
      : event.dateText || "",
    eventName: primaryHiddenForBanner ? "" : eventTitleText,
    locationName: eventLocationText,
    // With a banner, the strip covers the primary row, so Primary takes the
    // first secondary slot and pushes the organizer's own field along.
    leadSecondaryField: primaryHiddenForBanner ? {
      label: values.primaryLabel || "Event",
      value: eventTitleText,
    } : undefined,
    auxiliaryFields: appleAuxiliaryFields,
    primaryLabel: values.primaryLabel || "Event",
    secondaryLabel: values.secondaryLabel || DEFAULT_SECONDARY_LABEL,
    logoImageUrl: appleLogoUrl,
    heroImageUrl: values.appleBannerUrl || undefined,
    showQr: values.qrCodeEnabled,
    backgroundColor: values.backgroundColor,
    textColor: values.textColor,
    labelColor: values.labelColor,
  }

  // Google is an Event Ticket. The card front shows the event name, venue,
  // date/time, the ticket holder + ticket, the QR, and the hero band. The
  // venue maps from the builder's location (secondary) field — matching the
  // API — and falls back to the event's own venue. Text/label colors are
  // auto-derived by Google for contrast, so they are intentionally not passed.
  const googlePreviewProps = {
    brandName: googleBrandName,
    eventName: eventTitleText || event.name || "",
    dateText: event.dateText || "",
    timeText: event.timeText || "",
    venueText: eventLocationText,
    // Google labels this "Ticket Holder" natively, so it is always the guest —
    // never the Event Title, which is an event-level slot.
    holderName: showGuestName ? PLACEHOLDER_GUEST_NAME : "",
    ticketLabel: "",
    ticketText: "",
    // Google allows three card rows: date/time, the holder, then these two.
    auxiliaryRow: auxiliaryFields,
    logoImageUrl: googleLogoUrl,
    heroImageUrl: values.googleHeroUrl || undefined,
    showQr: values.qrCodeEnabled,
    backgroundColor: values.backgroundColor,
  }

  // Google prints the event title on the card itself now, so the details view
  // carries only the organizer's own auxiliary fields. Allen, on DIO-1002:
  // "I don't think we should show the Primary Field on the back of the Google
  // Pass."
  const googleDetailFields = auxiliaryFields

  return {
    applePreviewProps, googlePreviewProps, googleDetailFields, backSections,
  }
}

export const formValuesToWalletPassData = (
  values: WalletPassBuilderFormValues,
): WalletPassData => {
  // A field left on its default saves empty, which is what makes the pass track
  // the event; anything typed before the switch is deliberately dropped.
  // Both halves of the header come from the event under its default, so unlike
  // the other fields its label goes with the value.
  const customHeaderValue = resolveFieldPreviewValue(
    values.headerValueSource,
    values.headerStaticValue,
  )
  const header = values.headerMode === "override" ? {
    headerLabel: values.headerLabel || undefined,
    headerValueSource: customHeaderValue ? values.headerValueSource : "hide" as const,
    headerStaticValue: values.headerStaticValue || undefined,
  } : { headerValueSource: "static" as const }

  const [eventTitle, eventLocation] = SYMMETRIC_FIELDS.map((field) => resolveFieldMode(
    values[field.mode], values[field.staticValue], field.defaultSource,
  ))

  return {
    backgroundColor: values.backgroundColor,
    textColor: values.textColor,
    labelColor: values.labelColor,
    logoText: values.brandNameMode === "override" ? values.logoText || undefined : undefined,
    squareLogoUrl: values.squareLogoUrl || undefined,
    horizontalLogoUrl: values.horizontalLogoUrl || undefined,
    appleBannerUrl: values.appleBannerUrl || undefined,
    googleHeroUrl: values.googleHeroUrl || undefined,
    ...header,
    primaryLabel: values.primaryLabel || undefined,
    primaryValueSource: eventTitle.valueSource,
    primaryStaticValue: eventTitle.staticValue,
    primaryMovedBelowBanner: values.primaryMovedBelowBanner || undefined,
    secondaryLabel: values.secondaryLabel || undefined,
    secondaryValueSource: eventLocation.valueSource,
    secondaryStaticValue: eventLocation.staticValue,
    auxiliary1Label: values.auxiliary1Label || undefined,
    auxiliary1ValueSource: values.auxiliary1Enabled ? values.auxiliary1ValueSource : "hide",
    auxiliary1StaticValue: values.auxiliary1StaticValue || undefined,
    auxiliary2Label: values.auxiliary2Label || undefined,
    auxiliary2ValueSource: values.auxiliary2Enabled ? values.auxiliary2ValueSource : "hide",
    auxiliary2StaticValue: values.auxiliary2StaticValue || undefined,

    guestNameEnabled: values.guestNameEnabled,
    guestNameLabel: values.guestNameLabel || undefined,
    qrCodeEnabled: values.qrCodeEnabled,
    backSections: values.backSections
      .filter((section) => section.label.trim() || section.value.trim())
      .map((section, index) => ({
        id: section.id || `back-${index + 1}`,
        type: section.type || "text",
        label: section.label.trim(),
        value: normalizeBackSectionValue(section.type, section.value),
      })),
  }
}
